import { StakeModeType, useIntl, useSettings } from '@capsicum/common';
import { HeaderBlock, ShimmerButton, useDAppsRiskWarningActionGuard } from '@capsicum/common-web';
import {
	getChilizCardProps,
	type NCWalletChilizCardProps,
	useStakeAllFanTokensCTAEvent
} from '@capsicum/features/nc-wallet/shared';
import { useFanTokenStakingConfig } from '@capsicum/socios-core';
import { useFanTokenStakingRouter } from '@capsicum/socios-core-web';
import styled from 'styled-components';

import { useNCWPartnerRouting } from '../../hooks/useNCWPartnerRouting';
import { NCWalletTokenListCard } from '../nc-wallet-token-list-card/NCWalletTokenListCard';

/**
 * NCWalletChilizCard component renders a card displaying Chiliz token information.
 * It conditionally displays a stake button based on the provided props and settings.
 *
 * @param {boolean} isStakeButtonDisplay - Determines if the stake button should be displayed.
 * @param {NCWTokenBalanceType} chilizBalance - The balance information of the Chiliz token.
 *
 * @returns {JSX.Element} The rendered NCWalletChilizCard component.
 */
export const NCWalletChilizCard = ({
	chilizBalance = undefined,
	isStakeButtonDisplay = false
}: NCWalletChilizCardProps) => {
	const { $t } = useIntl();
	const {
		areSettingsLoading,
		settings: { isWalletConnectEnabled, stakeLink }
	} = useSettings();

	const { areConfigsLoading: areStakingConfigLoading, isAllowed: isStakeAllAllowed } = useFanTokenStakingConfig(
		StakeModeType.STAKE_ALL
	);

	const { routeToNcwPartnerDetail } = useNCWPartnerRouting();
	const { navigateToStakeAllFanTokens } = useFanTokenStakingRouter();
	const triggerDAppsRiskWarningGuardedAction = useDAppsRiskWarningActionGuard();
	const { sendStakeAllFanTokensCTAEvent } = useStakeAllFanTokensCTAEvent();

	const handleChilizCardClick = () => {
		if (!!chilizBalance && chilizBalance.amount > 0) routeToNcwPartnerDetail(chilizBalance.shortCode);
	};

	const specificChilizCardProps = {
		onBodyClick: handleChilizCardClick,
		onIconClick: handleChilizCardClick,
		onPrimaryButtonClick:
			isWalletConnectEnabled && isStakeButtonDisplay
				? () => triggerDAppsRiskWarningGuardedAction(() => window.open(stakeLink, '_blank'))
				: undefined,
		primaryButtonLabel:
			isWalletConnectEnabled && isStakeButtonDisplay
				? $t('staking.fanTokenStaking.chzStakeButtonLabel')
				: undefined
	};

	return (
		chilizBalance && (
			<div key={chilizBalance.shortCode}>
				<S.Header>
					<HeaderBlock testID="myassets-home-header" title={$t('ncWallet.home.tokensPageTitle')} />
					{isStakeAllAllowed && (
						<ShimmerButton
							color="brand"
							isLoading={areStakingConfigLoading || areSettingsLoading}
							shimmerAnimationIterations={1}
							size="xsmall"
							testID="stakeAllFanTokens-stakeAllCTA-button"
							text={$t('ncWallet.home.stakeAllFanTokens')}
							textSize="xsmall"
							onClick={() => {
								navigateToStakeAllFanTokens({});
								sendStakeAllFanTokensCTAEvent();
							}}
						/>
					)}
				</S.Header>
				<NCWalletTokenListCard
					{...getChilizCardProps(chilizBalance.tokenCardData)}
					{...specificChilizCardProps}
				/>
			</div>
		)
	);
};

namespace S {
	export const Header = styled.div`
		width: 100%;
		display: flex;
		justify-content: space-between;
	`;
}
